import { useCallback, useEffect, useState } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Loader2, Plus, Save, Trash2, Store, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';

const EMPTY = { name: '', account_lc: '', account_ld: '', active: true, pinned_lc: false, pinned_ld: false };

function SupplierRow({ s, onSave, onDelete, onMove, first, last }) {
  const [form, setForm] = useState(s);
  const [busy, setBusy] = useState(false);
  useEffect(() => setForm(s), [s]);
  const dirty = ['name', 'account_lc', 'account_ld', 'active', 'pinned_lc', 'pinned_ld'].some(k => (form[k] ?? '') !== (s[k] ?? ''));
  const save = async () => { setBusy(true); try { await onSave(s.id, form); } finally { setBusy(false); } };
  return (
    <tr className={`border-t border-white/5 [&>td]:px-3 [&>td]:py-2 ${form.active ? '' : 'opacity-60'}`} data-testid={`order-supplier-row-${s.id}`}>
      <td className="whitespace-nowrap">
        <div className="flex flex-col">
          <button type="button" disabled={first} onClick={() => onMove(s.id, -1)} className="text-gray-500 hover:text-white disabled:opacity-20" aria-label="Move up" data-testid={`order-supplier-up-${s.id}`}><ArrowUp className="w-3.5 h-3.5" /></button>
          <button type="button" disabled={last} onClick={() => onMove(s.id, 1)} className="text-gray-500 hover:text-white disabled:opacity-20" aria-label="Move down" data-testid={`order-supplier-down-${s.id}`}><ArrowDown className="w-3.5 h-3.5" /></button>
        </div>
      </td>
      <td><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="h-9 bg-[#1E2530] border-white/20 text-white min-w-[140px]" data-testid={`order-supplier-name-${s.id}`} /></td>
      <td><Input value={form.account_lc} onChange={e => setForm(f => ({ ...f, account_lc: e.target.value }))} placeholder="e.g. AR2135" className="h-9 bg-[#1E2530] border-blue-500/30 text-blue-200 font-mono uppercase min-w-[110px]" data-testid={`order-supplier-lc-${s.id}`} /></td>
      <td className="text-center"><Switch checked={!!form.pinned_lc} onCheckedChange={v => setForm(f => ({ ...f, pinned_lc: v }))} data-testid={`order-supplier-pinned-lc-${s.id}`} /></td>
      <td><Input value={form.account_ld} onChange={e => setForm(f => ({ ...f, account_ld: e.target.value }))} placeholder="e.g. MA2320" className="h-9 bg-[#1E2530] border-[#F5A623]/30 text-[#F5A623] font-mono uppercase min-w-[110px]" data-testid={`order-supplier-ld-${s.id}`} /></td>
      <td className="text-center"><Switch checked={!!form.pinned_ld} onCheckedChange={v => setForm(f => ({ ...f, pinned_ld: v }))} data-testid={`order-supplier-pinned-ld-${s.id}`} /></td>
      <td className="text-center"><Switch checked={form.active} onCheckedChange={v => setForm(f => ({ ...f, active: v }))} data-testid={`order-supplier-active-${s.id}`} /></td>
      <td className="text-right whitespace-nowrap">
        <Button size="sm" onClick={save} disabled={!dirty || busy} className="h-8 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] disabled:opacity-40" data-testid={`order-supplier-save-${s.id}`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => onDelete(s)} className="h-8 ml-1 text-gray-400 hover:text-red-400" data-testid={`order-supplier-delete-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
      </td>
    </tr>
  );
}

/** Settings → Order suppliers: the curated merchant list (account numbers per company, per-company Capture pins, order). */
export function OrderSuppliersTab({ api }) {
  const [items, setItems] = useState(null);
  const [draft, setDraft] = useState(EMPTY);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get('/order-suppliers', { params: { all: true } }); setItems(r.data || []); }
    catch { toast.error('Could not load order suppliers'); setItems([]); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const save = async (id, form) => {
    try { await api.put(`/order-suppliers/${id}`, form); toast.success(`${form.name} saved`); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.name} from the order suppliers list?`)) return;
    try { await api.delete(`/order-suppliers/${s.id}`); toast.success(`${s.name} removed`); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Remove failed'); }
  };
  const add = async () => {
    if (draft.name.trim().length < 2) { toast.error('Enter a supplier name'); return; }
    setAdding(true);
    try { await api.post('/order-suppliers', draft); toast.success(`${draft.name.trim()} added`); setDraft(EMPTY); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Add failed'); }
    finally { setAdding(false); }
  };
  const move = async (id, dir) => {
    const ids = items.map(s => s.id);
    const i = ids.indexOf(id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    setItems(ids.map(x => items.find(s => s.id === x)));
    try { const r = await api.put('/order-suppliers/reorder', { ids }); setItems(r.data || []); }
    catch (e) { toast.error(e.response?.data?.detail || 'Reorder failed'); await load(); }
  };

  return (
    <div className="space-y-4" data-testid="order-suppliers-tab">
      <div>
        <h3 className="text-lg font-semibold text-white flex items-center gap-2"><Store className="w-5 h-5 text-[#F5A623]" /> Order suppliers</h3>
        <p className="text-sm text-gray-400">The merchants the field team buy from — not the whole CRM. Each company has its own <span className="text-white">Capture button</span> switch: suppliers switched on appear as the big buttons at the top of Capture for that company, in the order below (use the arrows). The rest sit in the "Other supplier" list. <span className="text-blue-300">LC = LUX Construction</span> · <span className="text-[#F5A623]">LD = LUX Developments</span>.</p>
      </div>
      {items === null ? <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto" /></div> : (
        <div className="rounded-lg bg-[#2A3240] border border-white/10 overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="text-[11px] uppercase tracking-wide text-gray-500">
              <tr className="[&>th]:px-3 [&>th]:py-2 [&>th]:text-left">
                <th className="w-8">Order</th><th>Supplier</th>
                <th className="text-blue-300">Account · LC</th><th className="text-center text-blue-300">Capture button · LC</th>
                <th className="text-[#F5A623]">Account · LD</th><th className="text-center text-[#F5A623]">Capture button · LD</th>
                <th className="text-center">Available</th><th />
              </tr>
            </thead>
            <tbody>
              {items.map((s, i) => <SupplierRow key={s.id} s={s} onSave={save} onDelete={remove} onMove={move} first={i === 0} last={i === items.length - 1} />)}
              <tr className="border-t border-white/10 bg-[#1E2530]/60 [&>td]:px-3 [&>td]:py-2" data-testid="order-supplier-new-row">
                <td />
                <td><Input value={draft.name} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))} placeholder="New supplier, e.g. Toolstation" className="h-9 bg-[#1E2530] border-white/20 text-white min-w-[140px]" data-testid="order-supplier-new-name" /></td>
                <td><Input value={draft.account_lc} onChange={e => setDraft(d => ({ ...d, account_lc: e.target.value }))} placeholder="LC account no." className="h-9 bg-[#1E2530] border-white/20 text-blue-200 font-mono uppercase min-w-[110px]" data-testid="order-supplier-new-lc" /></td>
                <td className="text-center"><Switch checked={!!draft.pinned_lc} onCheckedChange={v => setDraft(d => ({ ...d, pinned_lc: v }))} data-testid="order-supplier-new-pinned-lc" /></td>
                <td><Input value={draft.account_ld} onChange={e => setDraft(d => ({ ...d, account_ld: e.target.value }))} placeholder="LD account no." className="h-9 bg-[#1E2530] border-white/20 text-[#F5A623] font-mono uppercase min-w-[110px]" data-testid="order-supplier-new-ld" /></td>
                <td className="text-center"><Switch checked={!!draft.pinned_ld} onCheckedChange={v => setDraft(d => ({ ...d, pinned_ld: v }))} data-testid="order-supplier-new-pinned-ld" /></td>
                <td className="text-center text-xs text-gray-500">on</td>
                <td className="text-right">
                  <Button size="sm" onClick={add} disabled={adding} className="h-8 bg-white/10 hover:bg-white/20 text-white" data-testid="order-supplier-add">
                    {adding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Plus className="w-3.5 h-3.5 mr-1" /> Add</>}
                  </Button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
